import { useState, useEffect, lazy } from 'react';
import { Lazy } from '../Lazy/Lazy';
import { signOut } from '../../services/signOut';
import { storeService } from '../../services/store.service';

// Lazy-loaded like the other route pages — this wrapper is already lazy.
const MyPackages = lazy(() => import('./MyPackages'));

/**
 * Route shell for "My packages": hydrates the signed-in user, the company
 * logo and the selected store from local storage, exactly as the
 * Appointments wrapper does, so the page renders inside the same chrome.
 */
export default function MyPackagesWrapper() {
  const [user, setUser] = useState<any | null>(null);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    const initData = async () => {
      const { ensureCMSUser } = await import('../../services/auth.service');
      const cmsUser = await ensureCMSUser();
      if (cmsUser) {
        setUser({
          name: `${cmsUser.firstName || ''} ${cmsUser.lastName || ''}`.trim(),
          email: cmsUser.email || '',
          picture: cmsUser.picture || ''
        });
      }

      let logoUrl = storeService.getCompanyLogoUrl();
      if (!logoUrl) {
        try {
          const { getCurrentDomain } = await import('../../utils/domain');
          const { CMS_BASE_URL, API_CONFIG, CMS_ENVIRONMENT_QUERY } = await import('../../config/api.config');
          const response = await fetch(
            `${CMS_BASE_URL}/api/companies/by-domain-name?domainName=${getCurrentDomain()}${CMS_ENVIRONMENT_QUERY}`,
            { method: 'GET', headers: API_CONFIG.headers }
          );
          if (response.ok) {
            const data = await response.json();
            logoUrl = data.companyPublicLogoUrl || null;
            if (logoUrl) {
              localStorage.setItem('company_logo_cache', logoUrl);
            }
          }
        } catch (error) {
          console.error('Error fetching company logo:', error);
        }
      }
      if (logoUrl) setCompanyLogoUrl(logoUrl);

      const storedStore = localStorage.getItem('selectedStore');
      if (storedStore) {
        try {
          setSelectedStore(JSON.parse(storedStore));
        } catch (e) {
          console.error('Error parsing stored store:', e);
        }
      }
    };

    initData();
  }, []);

  const handleLogin = (profile: any) => {
    setUser(profile);
    localStorage.setItem('cms_user', JSON.stringify(profile));
  };

  const handleLogout = () => {
    signOut();
    setUser(null);
  };

  const handleLocationSelect = (store: any) => {
    setSelectedStore(store);
    localStorage.setItem('selectedStore', JSON.stringify(store));
  };

  return (
    <Lazy>
      <MyPackages
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onLoginError={(error) => console.error('Login error:', error)}
        selectedStore={selectedStore}
        onLocationSelect={handleLocationSelect}
        companyLogoUrl={companyLogoUrl}
      />
    </Lazy>
  );
}
